// The Expenses & Other Revenue screens' rules: the add/edit drawer, the list,
// the category manager and the summary.
//
// Here rather than in `src/hooks/useExpenseEntryForm.js`,
// `src/hooks/useExpenseList.js`, `src/hooks/useCategoryList.js`,
// `src/pages/ExpenseCategoriesPage.jsx` and the scan notice and summary
// components, so the Android and iOS screens hold the same rules. Nothing here
// holds state or touches Firebase.
//
// **Adapters.** The rules are `core/src/expenses.rs`, so the phones' drawer
// refuses the same fields with the same sentences, and their list filters and
// orders the same rows. What stays here is the coercion — the form's loose
// values into the core's `ExpenseEntryForm`, and the answers back into the
// shapes the hooks hold — and the constants a module reads at load, which
// `test/core/expenses.wasm.test.js` holds to the core's.

import { coreSync } from './core.js';
import { EXPENSE_ATTACHMENTS_MAX, attachmentPaths, entryOf } from './expenses.js';

const text = (v) => (v == null ? null : String(v));

// The form as the core reads it: every box's text, the two ticks.
const formIn = (f) => ({
  payee: String(f?.payee ?? ''),
  category: String(f?.category ?? ''),
  amount: String(f?.amount ?? ''),
  isRefund: Boolean(f?.isRefund),
  description: String(f?.description ?? ''),
  recurrence: String(f?.recurrence ?? ''),
  frequency: String(f?.frequency ?? ''),
  interval: String(f?.interval ?? ''),
  repeatIndefinitely: Boolean(f?.repeatIndefinitely),
  endDate: String(f?.endDate ?? ''),
  occurredOn: String(f?.occurredOn ?? ''),
});

// The core's form back as the hook holds it: the interval a number, and the
// receipts — which never cross — beside it.
const formOut = (f, attachments = []) => ({
  payee: f.payee,
  category: f.category,
  amount: f.amount,
  isRefund: f.isRefund,
  description: f.description,
  recurrence: f.recurrence,
  frequency: f.frequency,
  interval: Number(f.interval),
  repeatIndefinitely: f.repeatIndefinitely,
  endDate: f.endDate,
  attachments,
  occurredOn: f.occurredOn,
});

const scanOf = (r) => ({
  payee: text(r?.payee),
  suggestedCategory: text(r?.suggestedCategory),
  amountMinor: r?.amountMinor == null ? null : Number(r.amountMinor),
  occurredOn: text(r?.occurredOn),
  amountSource: text(r?.amountSource),
});

// ── The add/edit drawer ────────────────────────────────────────────────────


// A blank entry, dated `today` — the caller's clock, since a rule has none.
export const blankEntryForm = (today) => formOut(coreSync().expenseBlankForm(String(today)));


// "Dirty" is what the guard keys off: has the form moved from what it opened
// with? For a new entry the baseline is blank, and the date is excluded while it
// still holds today's default — opening the form and closing it again has not
// changed anything worth keeping. For an edit the baseline is the entry, so
// re-saving it unchanged is not "unsaved work".
export function isEntryDirty(form, baseline, today) {
  // The whole list, in order (#104). Comparing the first path — or the count —
  // would call a form clean after a receipt was swapped for another, so closing
  // it would sweep the new object and leave the row naming the old.
  return coreSync().expenseFormDirty(
    formIn(form), attachmentPaths(form), baseline ? formIn(baseline) : null,
    baseline ? attachmentPaths(baseline) : [], String(today),
  );
}


// ── What Save refuses, and how it says so (#116) ────────────────────────────
//
// **Payee, Category and Amount are all required on an expense**, as in MMS,
// which refuses them on Save. The sentence is MMS's own, verbatim and
// identical under every field.
export const REQUIRED = 'This field is required';

// Ours; what MMS says here is not known. See MAX_MONEY_MINOR in
// domain/money.js for the number. It names no figure: a teacher who typed this
// typed an exponent by accident. The CSV wizard's message *does* name the
// ceiling, because there nobody typed the cell and the number says which
// column was mis-mapped.
export const AMOUNT_TOO_LARGE = 'That amount is too large to store.';

// Ours (#166). A refund is a flag on a positive amount, not a negative one
// (`signedMinor` in domain/expenses.js), so a typed minus is not a value this
// field has a meaning for.
export const AMOUNT_NEGATIVE = 'Amounts are not negative. Use "This is a refund" for money coming back.';

// Ours: a "Repeat Until" before the entry's own date — see the check at the
// end of `entryErrors`, which says why MMS has no sentence to borrow.
export const END_BEFORE_START = 'Repeating stops before the first one, so nothing would be created.';

// Ours, number included (#183): `EXPENSE_ATTACHMENTS_MAX` in
// domain/expenses.js argues the figure. MMS states no cap, so this differs from
// MMS and says the number. It sits under the attachment field rather than
// under Save: nothing about the entry is refused, only the next file.
export const ATTACHMENTS_FULL =
  `An expense takes up to ${EXPENSE_ATTACHMENTS_MAX} attachments. `
  + 'Remove one to add another.';

// Everything Save will refuse, keyed by the field it belongs under. Exported
// pure so the rule can be exercised without a drawer.
//
// **This is not `canSave`.** In MMS, Save is greyed only while the form is
// untouched and enables the moment anything is entered; clicking it then
// leaves the drawer open, saves nothing, and puts a red border and this
// sentence under each offending field. The interaction is matched as well as
// the rule (#116).
//
// The rules, `expense_entry_errors` in the core:
//
//   * **Required.** The empty string is the unset category (CATEGORY_UNSET in
//     src/domain/expenses.js). A zero amount is allowed — it is a refund line;
//     an empty field is refused.
//   * **Too large to store (#155).** `<input type="number">` accepts exponent
//     notation, and past `Number.MAX_SAFE_INTEGER` Firestore stores a double,
//     which the rules refuse as a permission error (#115). Judged on the typed
//     value's finiteness before `fromDecimal`, which returns **0** for a
//     non-finite input, and on magnitude, so `-1e300` gets this sentence.
//   * **Negative (#166)**, refused rather than left to `addEntry`'s
//     `Math.max(0, …)` clamp, which would save a typed `-5` as zero in
//     silence. `isRefund` is the sign. MMS drops the minus at the keystroke,
//     leaving a positive number the teacher did not type; we refuse and say so
//     (the same no-silent-correction rule as #131 and #132).
//   * **Date (#132).** `firestore.rules` requires `occurredOn` to match
//     `^[0-9]{4}-[0-9]{2}-[0-9]{2}$`, so an emptied date would come back as a
//     permission error. MMS blocks an emptied date on Save with no message at
//     all; we refuse it with MMS's required-field sentence.
//   * **Repeat Until before the entry's date.** MMS's picker greys those days
//     out; ours has the same `min`, and this catches a typed one, which would
//     otherwise save a rule that produces no instances. The wording is ours.
export function entryErrors(form, currency = 'USD') {
  const errors = {};
  for (const e of coreSync().expenseEntryErrors(formIn(form), text(currency))) errors[e.field] = e.message;
  return errors;
}

// An entry, or a derived instance of one, as the form wants it.
// `attachments` is an argument (#183), not a field on `e`.
//
// A receipt is its own document, so the entry a row was drawn from does not
// carry its own list. The caller — `ExpensesPage`, through
// `useExpenses().attachmentsFor` — has the answer, including the fallback for
// an entry whose receipts are still inline on its document.
//
// Defaulted to none rather than required, for the tests about recurrence and
// money. A caller who forgets opens the drawer with no receipts and, on save,
// **detaches every one of them**, so `openEdit` in useExpenseEntryForm.js
// requires it as a second argument.
export function formForEntry(e, currency = 'USD', attachments = []) {
  const f = formOut(coreSync().expenseFormForEntry({
    payee: text(e.payee),
    category: text(e.category),
    amountMinor: Number(e.amountMinor),
    isRefund: Boolean(e.isRefund),
    description: text(e.description),
    occurredOn: text(e.occurredOn),
    // A recurring rule is edited at its own start date, not at the instance the
    // teacher happened to click: the date is the rule's anchor, and writing back
    // the instance date would silently shift every other month.
    seriesStart: text(e.seriesStart),
    recurrence: text(e.recurrence),
    // Read as `formRecurrence` reads it, for the reason useEventForm does: a
    // rule stored as `biweekly` has no option left in the select, and the
    // fallback to the first one would silently make it daily on save.
    frequency: text(e.frequency),
    interval: text(e.interval),
    endDate: text(e.endDate),
  }, text(currency)), Array.isArray(attachments) ? attachments : []);
  // In the order the drawer's own form always had: the date after the text.
  const {
    payee, category, amount, isRefund, description, occurredOn, ...rest
  } = f;
  return { payee, category, amount, isRefund, description, occurredOn, ...rest };
}


// Whether Save is *clickable*, which on this form is a different question from
// whether Save will go through — see `entryErrors` above, and do not put a
// requirement here. What it mirrors is MMS: Save starts greyed on a pristine
// drawer and enables as soon as anything is entered.
//
// Written over the form's own content rather than over `isEntryDirty`, so an
// existing entry, which arrives with an amount, has the button live from the
// moment the drawer opens.
//
// The date is excluded because it arrives prefilled with today — counting it
// would make every new drawer look touched. Though the date is required
// (#132), clearing it on an otherwise untouched drawer leaves Save greyed; once
// anything else is entered, its refusal arrives with the rest.
export function entryCanSave(form) {
  return coreSync().expenseFormCanSave(formIn(form), (form.attachments || []).length);
}


// The amount Save writes, in minor units: the box read once, in the studio's
// currency. An emptied box is refused by `entryErrors` before this is asked.
export function entryAmountMinor(amount, currency = 'USD') {
  return coreSync().expenseFormAmountMinor(amount == null ? null : String(amount), text(currency));
}


// The refusals left standing once the teacher changes `fields`.
//
// A field the teacher is fixing stops being red as they fix it, rather than
// staying red until the next Save. Ours; MMS's behaviour here is not known.
//
// `endDate` is cleared by a change to `occurredOn` too, because that is the
// other half of its comparison: moving the start date is a legitimate way to
// fix a run that ended too early.
export function errorsAfterPatch(errors, fields) {
  if (!Object.keys(errors).length) return errors;
  const left = coreSync().expenseErrorsAfterChange(Object.keys(errors), fields.map(String));
  return Object.fromEntries(left.map((k) => [k, errors[k]]));
}


// The refusal the next file meets when `held` are already on the form, or
// null. Said under the attachment field, not under Save: nothing about the
// entry is refused, only the next file.
export function attachRefusal(held) {
  return coreSync().expenseAttachRefusal(Number(held) || 0)?.message ?? null;
}


// The form a scanned receipt opens: a new expense, filled from the service's
// reply, holding the one object the scan uploaded (none when that upload
// failed).
//
// Clamped, where the typed fields get a `maxLength` instead (#145): a
// service's reply arrives through `setForm`, which a `maxLength` does not
// constrain, and firestore.rules bounds both. The value was not typed, so
// there is nobody to warn.
//
// A scan builds a *new* form, holding at most the one object this scan
// uploaded. Anything else this sitting uploaded is the sweep's (#119).
//
// `categories` is the studio's expense categories: the reader's
// suggestion opens as the one it matches, in the studio's spelling, or as Not
// Specified — never as a name that is not a category.
export function scannedEntryForm(result, today, currency = 'USD', attachment = null, categories = []) {
  return formOut(
    coreSync().expenseScannedForm(scanOf(result), String(today), text(currency), (categories || []).map(String)),
    attachment ? [attachment] : [],
  );
}


// What the scan notice (`ScanNotice.jsx`) asks the teacher to check, in the
// order it says them: the doubtful fields by name.
export function scanUncertainties(scan) {
  return coreSync().expenseScanDoubts(scanOf(scan));
}

// `ScanNotice.jsx`'s sentence: filled in from the receipt, the doubtful fields,
// and the tax the total includes, written by the caller in the studio's money
// (#472).
export function scanNoticeText(scan, taxText) {
  return coreSync().expenseScanNotice(scanUncertainties(scan), taxText == null ? null : String(taxText));
}


// The drawer's title: `Add Expense`, `Edit Revenue`.
export function entryDrawerTitle(type, editing) {
  return coreSync().expenseDrawerTitle(text(type), Boolean(editing));
}


// Revenue is paid *by* someone, so the same field is Payer there.
export function payeeLabel(type) {
  return coreSync().expensePayeeLabel(text(type));
}


// The two Recurrence radios: `One-time expense`, `Recurring revenue`.
export function recurrenceChoices(type) {
  return coreSync().expenseRecurrenceChoices(text(type)).map(({ id, label }) => ({ id, label }));
}


// A row's two actions. A row drawn from a recurring rule edits and deletes the
// rule: instances are derived, so there is no single month to remove.
export function entryRowActions(isSeries) {
  const { edit, remove } = coreSync().expenseRowActions(Boolean(isSeries));
  return { edit, remove };
}


// ── The list ───────────────────────────────────────────────────────────────

// The columns MMS offers in its Sort menu. Attachment and Description are not
// among them, and the menu is alphabetical rather than in column order.
export const EXPENSE_SORT_COLUMNS = [
  { key: 'category',   label: 'Category' },
  { key: 'occurredOn', label: 'Date' },
  { key: 'expense',    label: 'Expense' },
  { key: 'payee',      label: 'Payee' },
  { key: 'revenue',    label: 'Revenue' },
];

export const BLANK_FILTER = { text: '', category: '', amount: '' };

// The rows the list shows: the instances in range, narrowed by the Search
// menu, in the chosen order. An unknown sort key (Clear Sort) leaves the
// instances in the order they came.
const filterOf = (f) => ({
  text: String(f?.text ?? ''),
  category: String(f?.category ?? ''),
  amount: String(f?.amount ?? ''),
});

export function expenseListRows(instances, filter, sort, currency = 'USD') {
  // Rows with nothing in the sorted column sink to the bottom either way: an
  // expense has no revenue, and pushing those to the top when sorting by
  // Revenue buries the rows being asked for. The Amount search is exact, in
  // the studio's currency — "40" and "40.00" both find a $40 row, and in yen
  // "40" finds ¥40 rather than ¥4,000.
  const at = coreSync().expenseListRows(
    instances.map(entryOf), filterOf(filter), { key: text(sort?.key), desc: Boolean(sort?.desc) }, text(currency),
  );
  return Array.from(at, (i) => instances[i]);
}


// Whether the Search menu is narrowing the list — the empty state says a
// different thing when it is.
export function expenseFilterActive(filter) {
  return coreSync().expenseFilterActive(filterOf(filter));
}


// Choosing a column: the same one again reverses it, a new one starts
// ascending.
export function nextExpenseSort(current, key) {
  const next = coreSync().expenseNextSort({ key: text(current?.key), desc: Boolean(current?.desc) }, String(key));
  return { key: next.key, desc: next.desc };
}


// The Search menu's category list: both types' names, once each, in order.
export function allExpenseCategories(expenseCategories, revenueCategories) {
  return coreSync().expenseAllCategories(expenseCategories.map(String), revenueCategories.map(String));
}


// Said above the list when a recurring rule had more instances in range than
// one expansion returns, so truncated output does not look complete.
export function truncatedWarning(count) {
  return coreSync().expenseTruncatedWarning(Number(count) || 0) ?? null;
}


// The empty list: a studio with nothing yet, or a search that matches nothing.
export function expenseEmptyState(filtered) {
  const { title, description, action } = coreSync().expenseEmptyState(Boolean(filtered));
  return { title, description, action };
}


// ── The summary ────────────────────────────────────────────────────────────

// What each basis means, said under the summary (SummaryPanel.jsx).
export function basisNote(basis) {
  return coreSync().expenseBasisNote(text(basis));
}


// ── The category manager ───────────────────────────────────────────────────

// The category manager's Sort menu (`useCategoryList.js`).
export const CATEGORY_SORTS = [
  { key: 'label', label: 'Name' },
  { key: 'type', label: 'Type' },
  { key: 'count', label: 'Transactions' },
];

// The manager's rows: every category with the number of entries filed under
// it, in the chosen order. `usage` is `categoryUsage`'s map.
export function categoryListRows(records, usage, sort) {
  const counts = [...usage.entries()].map(([key, count]) => {
    const colon = key.indexOf(':');
    return { entryType: key.slice(0, colon), category: key.slice(colon + 1), count };
  });
  const crossing = records.map((c) => ({
    id: String(c.id ?? ''),
    entryType: String(c.type ?? ''),
    label: String(c.label ?? ''),
    isDefault: Boolean(c.isDefault),
    isSystem: Boolean(c.isSystem),
    stored: null,
  }));
  return coreSync()
    .expenseCategoryRows(crossing, counts, String(sort?.key ?? ''), Boolean(sort?.desc))
    .map(({ index, count }) => ({ ...records[index], count }));
}


// The same column again reverses it; a new one starts ascending.
export function nextCategorySort(current, key) {
  const next = coreSync().expenseNextSort({ key: text(current?.key), desc: Boolean(current?.desc) }, String(key));
  return current?.key === key ? { ...current, desc: next.desc } : { key: next.key, desc: next.desc };
}


// The delete confirmation. A category in use says what happens to its entries:
// they keep their amounts and read as Not Specified.
export function categoryDeleteMessage(label, count) {
  return coreSync().expenseCategoryDeleteMessage(String(label), Number(count) || 0);
}


// Whether Delete is offered: not on the category family payments file into,
// which removing would leave with nowhere to land.
export function categoryDeletable(category) {
  return coreSync().expenseCategoryDeletable(Boolean(category?.isSystem));
}


// The number of pages a list of `total` rows takes at `perPage` — never none.
export function pageCount(total, perPage) {
  return coreSync().expensePageCount(Number(total) || 0, Number(perPage) || 0);
}

